<template>
  <PageBreadcrumbs :items="breadcrumbs" />
  <ContentCard :loading="loading">
    <template #header>
      <ContentCardSubheader :back-to="{ name: ReviewsRouteName.ReviewsProductPreferencesView }">
        {{ t('Reviews.product.edit.preferencesTitle', { productName: product.title }) }}
        <template #actions>
          <RouterLink
            class="btn-secondary-color-s mr-2"
            data-testid="reviews-preferences-cancel-button"
            :to="{ name: ReviewsRouteName.ReviewsProductPreferencesView, params: { productId: product.id } }"
            @click="track('profile_update_cancel', { surface: 'editor', entity_type: 'tool', entity_id: product.id })"
          >
            {{ t('Reviews.product.action.cancel') }}
          </RouterLink>
          <button class="btn-primary-color-s" data-testid="reviews-preferences-save-button" @click="onSubmit">
            {{ t('Reviews.product.action.save') }}
          </button>
        </template>
      </ContentCardSubheader>
    </template>

    <div class="flex flex-col gap-y-4 pt-6 pb-10">
      <div class="flex items-start pb-6 text-main">
        <MdiSvg class="mr-2 inline-block" :path="mdiInformationOutline" />
        {{ t('Reviews.product.edit.preferences.hint') }}
      </div>
      <PreferenceItemEdit
        id="vendor-email-form"
        v-model="form.vendorEmailAddresses"
        :description="t('Reviews.product.view.preferences.vendorEmails.description')"
        :title="t('Reviews.product.view.preferences.vendorEmails.title')"
        data-testid="reviews-pref-vendor-emails"
      />
      <PreferenceItemEdit
        id="contact-email-form"
        v-model="form.contactFormRecipients"
        :description="t('Reviews.product.view.preferences.contactEmails.description')"
        :title="t('Reviews.product.view.preferences.contactEmails.title')"
        data-testid="reviews-pref-contact-emails"
      />
      <PreferenceItemEdit
        id="page-visits-email-form"
        v-model="form.pageVisitReportEmails"
        :description="t('Reviews.product.view.preferences.pageVisitReportEmails.description')"
        :title="t('Reviews.product.view.preferences.pageVisitReportEmails.title')"
        data-testid="reviews-pref-edit-emails"
      />
    </div>
  </ContentCard>
</template>

<script setup lang="ts">
import { mdiInformationOutline } from '@mdi/js'
import { MdiSvg } from '@ramp106/omrjs-core-ui'
import { track } from '@ramp106/omrjs-tracking-reviews'
import { inject, reactive } from 'vue'
import { useI18n } from 'vue-i18n'
import { useRouter } from 'vue-router'

import PageBreadcrumbs from '@/components/PageBreadcrumbs.vue'
import PreferenceItemEdit from '@/components/ReviewsManagement/Preferences/PreferenceItemEdit.vue'
import { useReviewsProductBreadcrumbs } from '@/composables/breadcrumbs/useReviewsProductBreadcrumbs'
import { type ProductData, useUpdateReviewsProductMutation } from '@/gql/reviews'
import { showNotification } from '@/helpers/notificationHelper'
import { ReviewsRouteName } from '@/router/types'
import { reloadReviewsProductKey } from '@/symbols/reviewsReloadKeys'
import ContentCard from '@/ui/ContentCard.vue'
import ContentCardSubheader from '@/ui/ContentCardSubheader.vue'

const props = defineProps<{
  product: ProductData
}>()

const { t } = useI18n()
const router = useRouter()
const reloadReviewsProduct = inject(reloadReviewsProductKey) as () => void
const breadcrumbs = useReviewsProductBreadcrumbs(props.product)

const {
  mutate: mutateProduct,
  loading,
  onDone,
  onError,
} = useUpdateReviewsProductMutation({
  clientId: 'reviews',
})

const form = reactive({
  vendorEmailAddresses: props.product.vendorEmailAddresses ? [...props.product.vendorEmailAddresses] : [],
  contactFormRecipients: props.product.contactFormRecipients ? [...props.product.contactFormRecipients] : [],
  pageVisitReportEmails: props.product.pageVisitReportEmails ? [...props.product.pageVisitReportEmails] : [],
})

async function onSubmit() {
  track('profile_update', { surface: 'editor', entity_type: 'tool', entity_id: props.product.id })
  await mutateProduct({
    id: props.product.id,
    attributes: {
      vendorEmailAddresses: form.vendorEmailAddresses,
      contactFormRecipients: form.contactFormRecipients,
      pageVisitReportEmails: form.pageVisitReportEmails,
    },
  })
}

onDone(() => {
  reloadReviewsProduct()
  showNotification(t('Reviews.product.notifications.savedSuccessfully', { name: props.product.title }), 'success')
  router.push({ name: ReviewsRouteName.ReviewsProductPreferencesView, params: { productId: props.product.id } })
})

onError((error) => showNotification(error.message, 'error'))
</script>
